import {Divider} from "@/components/divider/Divider.tsx";
import {TooltipIconRA} from "@/components/overlay/TooltipIconRA.tsx";
import {SAMLSettings} from "@/lib/types/statuspages.ts";

import {ActiveDirectoryHint} from "./ActiveDirectoryHint.tsx";
import {SAMLConfigLine} from "./SAMLConfigLine.tsx";

type SSOSetupStep1Props = {
  metadataURL: string;
  samlSettings: SAMLSettings;
  variant?: "general" | "tab";
};

export const SSOSetupStep1 = ({metadataURL, samlSettings, variant = "general"}: SSOSetupStep1Props) => (
  <>
    <>
      <div className="pulseTextH3Bold mb-3">SAML Configuration</div>
      <p className="pulseTextBodySmall">
        Configure SAML SSO for your organization. Need Help?{" "}
        <a
          target="_blank"
          href="https://support.uptime.com/hc/en-us/articles/360001434205-Troubleshooting-Single-Sign-On-SSO-"
        >
          See our SAML SSO support article.
        </a>
      </p>
    </>
    <Divider className="my-4" />
    <div className="pulseTextH3Bold mb-3">Step 1: Add The Uptime.com Application In Your Identity Provider</div>
    <p className="pulseTextBodySmall mb-5">
      Please use these values to register the app in your identity provider.
      <br />
      Alternatively, you may download our metadata:{" "}
      <a href={metadataURL} download>
        Uptime.com Service Provider Metadata XML File
      </a>
    </p>
    <SAMLConfigLine
      className="mb-4"
      text="EntityID / Audience URI"
      tooltip="This is the value you'll need to enter in the 'Entity ID' field in your Identity Provider's configuration. This may also be referred to as 'Audience Restriction' or 'Audience URI'."
      code={samlSettings.sp_entity_id}
      variant={variant}
    />
    <SAMLConfigLine
      className="mb-4"
      text="ACS URL / Consumer URL / Recipient"
      tooltip="This is the URL is where your Identity Provider will send SAML assertions, and will be required in your Identity Provider's admin when setting up an Uptime.com app."
      code={samlSettings.sp_acs_url}
      variant={variant}
    />
    <SAMLConfigLine
      className="mb-2"
      text="WAYFless URL"
      tooltip="A URL that can be used to directly login to this Uptime.com account via your identity provider."
      code={samlSettings.sp_wayfless_url}
      variant={variant}
      optional
    />
    <div>
      <dt className="mt-3 d-flex">
        <div className="pulseTextH3Bold mb-3">Required SAML Attributes</div>
        <TooltipIconRA title="Attributes that must be sent by your identity provider to identify the user being authenticated." />
      </dt>
      <dd className="">
        <p className="mb-4 pulseTextBodySmall">
          We require the following attributes to be set to successfully authenticate the user:
        </p>
        <ul className="pulseTextBodySmall ps-4">
          <li>
            A unique user identifier or email address:{" "}
            <span className="pulseTextBodySmallBold">Name ID &nbsp;/&nbsp; Subject NameID</span>
          </li>
          <li>
            The user's email one of:{" "}
            <span className="pulseTextBodySmallBold">
              Email / User.Email / E-Mail Address / EmailAddress / Mail / eduPersonPrincipalName
            </span>
          </li>
          <li>
            The user's first name one of:{" "}
            <span className="pulseTextBodySmallBold">FirstName / User.FirstName / Given Name / givenName</span>
          </li>
          <li>
            The user's last name one of:{" "}
            <span className="pulseTextBodySmallBold">LastName / User.LastName / Surname / sn</span>
          </li>
        </ul>
      </dd>
    </div>
    <ActiveDirectoryHint />
  </>
);
